/**
 * Portal Context
 * Fetches and provides Noosa T Shirts' portal configuration from the shared Mango backend API.
 * Forked from mangoes-unified-template's subdomain-detection version — this app is single-tenant
 * (Noosa only), so the slug is hardcoded rather than derived from the hostname.
 *
 * Created by: Zee
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiBaseUrl } from '../services/api';

const PortalContext = createContext();

export const usePortal = () => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within PortalProvider');
  }
  return context;
};

// Default section visibility - all sections available
const defaultSections = {
  hero: true,
  products: true,
  services: true,
  events: true,
  gallery: true,
  about: true,
  design: true,
  team: true,
  testimonials: true,
  contact: true,
  newsletter: true,
  blog: true,
  faq: true,
  booking: true,
  liveChat: false,
  socialFeed: true,
};

// Default branding
const defaultBranding = {
  logoUrl: null,
  primaryColor: '#FFA500',
  secondaryColor: '#1a1a2e',
  accentColor: '#FF6B35',
  businessName: 'My Business',
};

export const PortalProvider = ({ children }) => {
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState(defaultSections);
  const [branding, setBranding] = useState(defaultBranding);

  useEffect(() => {
    const loadPortalConfig = async () => {
      try {
        // Single-tenant app — always Noosa's own portal, regardless of hostname/domain.
        const slug = 'noosa-t-shirts';
        const response = await fetch(`${getApiBaseUrl()}/portals/slug/${slug}`);
        if (!response.ok) {
          throw new Error('Portal not found');
        }

        const data = await response.json();
        const portalData = data.portal;

        // Set portal data
        setPortal(portalData);

        // Apply branding
        const newBranding = {
          logoUrl: portalData.logoUrl || portalData.business?.logoUrl,
          primaryColor: portalData.primaryColor || portalData.business?.primaryColor || defaultBranding.primaryColor,
          secondaryColor: portalData.secondaryColor || portalData.business?.secondaryColor || defaultBranding.secondaryColor,
          accentColor: portalData.accentColor || defaultBranding.accentColor,
          businessName: portalData.name || portalData.business?.name || defaultBranding.businessName,
        };
        setBranding(newBranding);

        // Apply CSS variables
        document.documentElement.style.setProperty('--primary-color', newBranding.primaryColor);
        document.documentElement.style.setProperty('--secondary-color', newBranding.secondaryColor);
        document.documentElement.style.setProperty('--accent-color', newBranding.accentColor);

        // Update page title
        document.title = newBranding.businessName;

        // Parse section visibility from funnelConfig or landingPage
        if (portalData.funnelConfig?.sections) {
          setSections({ ...defaultSections, ...portalData.funnelConfig.sections });
        } else if (portalData.landingPage?.sections) {
          setSections({ ...defaultSections, ...portalData.landingPage.sections });
        }

        console.log('[Portal] Loaded config for:', newBranding.businessName);
      } catch (err) {
        console.error('[Portal] Failed to load config:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPortalConfig();
  }, []);

  // Check if a section should be visible
  const isSectionVisible = (sectionName) => {
    return sections[sectionName] !== false;
  };

  // Toggle section visibility (for admin editing)
  const toggleSection = (sectionName) => {
    setSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Preview-site access (2026-09-21) - Dashboard + Presentation nav tabs only render once a
  // prospect has entered (or arrived with, via ?key=) their secret presentationAccessKey. See
  // shared-ui's PreviewKeyGate, which does the actual validation - this just decides whether the
  // nav links are worth showing at all, so the storage key format must match PreviewKeyGate's.
  const businessId = portal?.business?.id;
  const hasPreviewAccess = (() => {
    if (!businessId) return false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('key')) return true;
      return !!window.localStorage.getItem(`mango_preview_key_${businessId}`);
    } catch {
      return false;
    }
  })();

  const value = {
    portal,
    loading,
    error,
    sections,
    branding,
    isSectionVisible,
    toggleSection,
    products: portal?.products || [],
    services: portal?.services || [],
    businessId,
    hasPreviewAccess,
  };

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
};
